<script lang="ts">
    import Fa from 'svelte-fa'
    import { faStoreSlash, faTimes } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import { fullscreenLayers, openPopovers, popoverIndex } from './stores'
    import { fade, blur, fly, slide, scale } from 'svelte/transition'
    import { cubicOut, linear } from 'svelte/easing'
    const dispatch = createEventDispatcher()

    export let width: string = '600px'
    export let height: string = '200px'
    export let title: string = ''
    export let close: boolean = true
    export let slow = false
    export let padding: string = '0px'

    const isMobile = window.api.utils.utils.isMobile() && screen.width <= 900

    // ---- bottom sheet (só no mobile) -------------------------------------------------------
    // No mobile o popover vira uma folha ancorada na base: entra de baixo pra cima, tem cantos
    // superiores arredondados, alça de arraste e fecha por gesto ou toque no scrim. Com close={false}
    // (onboarding) nada disso é oferecido — a folha não pode ser dispensada, então exibir alça ou
    // aceitar o swipe seria mentir pro usuário.
    const canDismiss = isMobile && close

    let sheetEl: HTMLElement = null
    let dragging = false
    let dragOffset = 0
    let dragStartY = 0
    let dragStartAt = 0
    // enquanto fecha por gesto a folha já está fora da tela pelo nosso transform; deixar o outro do
    // Svelte rodar faria ela voltar pro lugar (o fly reposiciona em 0 antes de animar) e piscar
    let closingByGesture = false

    // Folhas empilhadas (um popover aberto de dentro de outro) ficam aninhadas no DOM, então o toque
    // na folha de cima sobe e chega aos handlers da de baixo — as duas arrastavam e fechavam juntas.
    // O gesto só vale para a folha mais interna sob o dedo.
    // uma folha só pode ser fechada pelo gesto enquanto ela é o que está à vista
    $: coveredByFullscreen = $fullscreenLayers > 0

    function isOwnSheet(target: EventTarget) {
        const node = target as HTMLElement
        return typeof node?.closest === 'function' && node.closest('.popover_sheet') === sheetEl
    }

    // Superfície em que o arrasto do dedo É o conteúdo: assinatura, marcação sobre foto. Ali o gesto
    // pertence ao desenho, não à folha — sem isto, assinar no celular arrastava o popover para baixo
    // em vez de riscar. `[data-no-sheet-drag]` é a saída para o que não é canvas (um slider, por ex.).
    function isDrawingSurface(target: EventTarget) {
        const node = target as HTMLElement
        return typeof node?.closest === 'function' && !!node.closest('canvas, [data-no-sheet-drag]')
    }

    // Percorre o caminho entre o alvo do toque e a folha dizendo se há área rolável no meio (o dedo
    // está no conteúdo, e não na alça/cabeçalho) e se alguma delas já está fora do topo.
    function scrollersBetween(target: EventTarget) {
        let node = target as HTMLElement
        let encontrou = false
        let rolada = false
        while (node && node !== sheetEl) {
            const overflow = getComputedStyle(node).overflowY
            if (overflow === 'auto' || overflow === 'scroll') {
                encontrou = true
                if (node.scrollTop > 0) rolada = true
            }
            node = node.parentElement
        }
        return { encontrou, rolada }
    }

    // Carência depois da última rolagem. Sem ela, quem está voltando ao topo de uma lista longa com
    // vários arrastos seguidos fecha a folha sem querer: num dos arrastos o conteúdo chega ao zero
    // (ou a inércia o leva até lá entre um toque e outro) e o arrasto seguinte já nasce "no topo",
    // virando gesto de dispensar. Enquanto a rolagem está acontecendo, o gesto pertence à lista.
    const SCROLL_COOLDOWN_MS = 400
    // distância mínima para um peteleco rápido valer como "fechar"
    const MIN_FLICK_PX = 80
    let lastScrollAt = 0

    function onSheetScroll() {
        lastScrollAt = Date.now()
    }

    function onTouchStart(ev: TouchEvent) {
        if (!canDismiss || ev.touches.length !== 1) return
        // com uma camada cobrindo a tela (a lista de um select ou de uma busca), o dedo está nela,
        // não na folha: arrastar ali fechava a folha que estava escondida atrás
        if (coveredByFullscreen) return
        if (!isOwnSheet(ev.target)) return
        if (isDrawingSurface(ev.target)) return
        const { encontrou, rolada } = scrollersBetween(ev.target)
        if (encontrou) {
            // a partir do conteúdo, só com a lista já no topo e parada
            if (rolada) return
            if (Date.now() - lastScrollAt < SCROLL_COOLDOWN_MS) return
        }
        dragging = true
        dragOffset = 0
        dragStartY = ev.touches[0].clientY
        dragStartAt = Date.now()
    }

    function onTouchMove(ev: TouchEvent) {
        if (!dragging) return
        const delta = ev.touches[0].clientY - dragStartY
        // só para baixo: arrastar pra cima não estica a folha
        dragOffset = delta > 0 ? delta : 0
        // segura o scroll da tela de trás enquanto o dedo está arrastando a folha
        if (dragOffset > 0 && ev.cancelable) ev.preventDefault()
    }

    function onTouchEnd() {
        if (!dragging) return
        dragging = false
        const altura = sheetEl?.getBoundingClientRect().height || 1
        const velocidade = dragOffset / Math.max(Date.now() - dragStartAt, 1) // px por ms
        // Fecha num arrasto longo ou num peteleco rápido — mas o peteleco também precisa ter andado
        // um tanto: só a velocidade fechava a folha com o movimento curto e ligeiro de quem está
        // rolando a lista de volta ao topo.
        if (dragOffset > altura * 0.25 || (velocidade > 0.6 && dragOffset > MIN_FLICK_PX)) {
            closingByGesture = true
            dragOffset = altura
            setTimeout(() => dispatch('close'), 180)
            return
        }
        dragOffset = 0
    }

    let transitionOptions: any = { delay: slow ? 150 : 50, duration: slow ? 250 : 100 }
    if (isMobile) {
        transitionOptions = { delay: slow ? 150 : 50, duration: slow ? 500 : 200, easing: linear, x: 250 }
    }

    function transition(node) {
        if (isMobile) {
            if (closingByGesture) return { duration: 0 }
            return fly(node, { delay: slow ? 150 : 50, duration: slow ? 400 : 220, easing: cubicOut, y: window.innerHeight })
        }
        return fade(node, { delay: slow ? 150 : 50, duration: slow ? 250 : 100 })
    }

    let localIndex: number = 1000
    let index: number = 1000
    popoverIndex.subscribe((value) => {
        index = value
    })

    onMount(() => {
        // enquanto houver folha aberta o app não se recarrega por conta própria (ver init.ts)
        openPopovers.update((value) => value + 1)
        popoverIndex.update((value) => {
            index = value + 2
            localIndex = index
            return index
        })
    })

    onDestroy(() => {
        openPopovers.update((value) => Math.max(0, value - 1))
        popoverIndex.update((value) => {
            if (value > 1000) index = value - 1
            return index
        })
    })

    function onClose() {
        dispatch('close')
    }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<!-- O popover flutua sobre a tela, mas no DOM ele é filho de onde foi declarado — e ali pode haver
     quem escute clique. Na agenda isso vazava: a célula do horário escuta clique para "agendar com
     1 clique", então clicar em qualquer lugar do modal "O que você deseja fazer?" abria a tela de
     agendamento por baixo. Nada que acontece dentro de uma camada modal pertence a quem está
     embaixo dela, então click/mousedown/mouseup param aqui. Os handlers de dentro do popover não
     são afetados: eles rodam antes, na descida até o alvo e na subida até este elemento. -->
<div
    class="popover_content_outer popover_content_outer_is-mobile_{isMobile}"
    class:is-closing={closingByGesture}
    style="z-index:{localIndex};"
    on:click|stopPropagation
    on:mousedown|stopPropagation
    on:mouseup|stopPropagation
>
    <!-- o toque na faixa de cima (o scrim) não fecha mais nada: era fácil demais acertá-la sem
         querer, e quem fecha são a alça, o gesto e os botões da própria tela -->
    <div class="z-popover popover_content scroll-dark" in:fade={{ duration: slow ? 200 : 100 }} out:fade={{ duration: closingByGesture ? 0 : 300 }}>
        <div
            bind:this={sheetEl}
            style="width:{width};min-height:{height};{dragOffset ? `transform:translateY(${dragOffset}px)` : ``}"
            class="content-block popover_sheet"
            class:is-dragging={dragging}
            transition:transition
            on:scroll|capture={onSheetScroll}
            on:touchstart={onTouchStart}
            on:touchmove|nonpassive={onTouchMove}
            on:touchend={onTouchEnd}
            on:touchcancel={onTouchEnd}
        >
            {#if canDismiss}
                <div class="sheet-handle"><div class="sheet-handle_bar"></div></div>
            {/if}
            <div class="z-popover_head">
                <div class="title">{title}</div>
                {#if close && !isMobile}
                    <button class="z-popover_close h-print" on:click={onClose}>
                        <Fa icon={faTimes} />
                    </button>
                {/if}
            </div>
            <div class="z-popover_body" style="padding:{padding};">
                <slot />
            </div>
        </div>
    </div>
</div>

<style>
    .popover_content_outer {
        max-width: 100% !important;
        position: fixed;
        margin-top: 0px;
        top: 0px;
        left: 0px;
        width: 100vw;
        height: 100vh;
        /* dvh desconta a barra do navegador: com vh a folha vazava para fora da área visível */
        height: 100dvh;
        background: #88888880;
        backdrop-filter: blur(1px);
        color: rgb(238, 238, 238);
        justify-content: center;
        align-items: flex-start;
    }
    /* o scrim também vale no mobile: é ele que dá a leitura de folha sobreposta e recebe o toque
       de fechar. Antes ficava transparente porque o popover ocupava a tela inteira. */
    .popover_content_outer_is-mobile_true {
        background: #00000066;
    }
    .popover_content {
        overflow-y: auto;
        height: 100%;
        padding: 48px 16px;
    }

    .content-block {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        background: rgb(22, 22, 22);
        border-radius: 8px;
        box-shadow: #000000 0px 22px 30px -15px;
        border: 1px solid #88888833;
        border-bottom: 0px;
        max-width: 100%;
        margin-left: auto;
        margin-right: auto;
    }
    .content-block .z-popover_head {
        display: flex;
        flex: 0 0 auto;
        overflow: hidden;
        border-radius: 8px 8px 0px 0px;
        padding: 8px;
    }
    .content-block .z-popover_head .title {
        color: #eee;
        flex-grow: 1;
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 300;
        padding: 4px 8px;
    }
    .content-block .z-popover_head button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 36px;
        height: 36px;
        background: #00000022;
        color: #ddd;
        border: 0px;
    }
    .content-block .z-popover_body {
        flex-grow: 1;
        background: #99999910;
        display: flex;
        flex-direction: column;
        border-radius: 0px 0px 8px 8px;
    }

    /* ---- bottom sheet ---- */
    .popover_content_outer_is-mobile_true .popover_content {
        padding: 0px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        /* a folha encosta na base e o scrim sobra no topo, que é onde se toca pra fechar */
        justify-content: flex-end;
    }

    .popover_content_outer_is-mobile_true .content-block {
        border: 1px solid #88888833;
        border-bottom: 0px;
        border-radius: 16px 16px 0px 0px;
        /* altura definida (e não "auto"): várias telas dentro do popover têm filhos com
               height:100%, que colapsariam se a folha passasse a se ajustar ao conteúdo */
        /* a regra base tem flex-grow:1 (inofensivo no desktop, onde o pai não é flex). Aqui o pai
               virou uma coluna flex, e sem zerar isso a folha se estica e ignora a altura abaixo. */
        flex: 0 0 auto;
        /* sempre sobra uma faixa da tela de trás à mostra, escurecida pelo scrim.
               dvh (e não vh) porque no mobile o vh ignora a barra do navegador e a faixa sumia. */
        height: 90vh;
        height: 90dvh;
        /* o min-height vem inline (prop "height") e serve ao desktop; aqui quem manda é a
               altura da folha, senão um valor maior a esticaria para fora do scrim */
        min-height: 0px !important;
        max-width: 100% !important;
        box-shadow: #000000 0px -8px 24px -12px;
        transition: transform 0.18s ease-out;
    }
    /* enquanto o dedo está na tela a folha acompanha o movimento sem suavização */
    .popover_content_outer_is-mobile_true .content-block.is-dragging {
        transition: none;
    }

    /* o overlay todo (scrim + folha) esmaece no mesmo tempo do deslize de saída, e só depois
           o componente é desmontado — é o que evita a piscada no fim do swipe */
    .popover_content_outer_is-mobile_true.popover_content_outer.is-closing {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.18s ease-out;
    }

    .popover_content_outer_is-mobile_true .sheet-handle {
        flex: 0 0 auto;
        display: flex;
        justify-content: center;
        /* área de toque maior que o traço, pra pegar o gesto com folga */
        padding: 10px 0px 2px 0px;
        cursor: grab;
    }
    .popover_content_outer_is-mobile_true .sheet-handle_bar {
        width: 36px;
        height: 4px;
        border-radius: 2px;
        background-color: #ffffff40;
    }
    /* no tema claro a folha é branca e a alça clara sumiria */
    :global(.theme_light) .popover_content_outer_is-mobile_true .sheet-handle_bar {
        background-color: #00000030;
    }

    .popover_content_outer_is-mobile_true .content-block .z-popover_head {
        padding: 4px 8px 8px 8px;
        background-color: unset;
        border-radius: 0px;
    }
    .popover_content_outer_is-mobile_true .content-block .z-popover_body {
        overflow-y: auto;
        border-radius: 0px;
        /* respeita a faixa do indicador de home no iOS */
        padding-bottom: env(safe-area-inset-bottom);
    }
</style>
